import { useCallback } from "react";
import { Form, Formik } from "formik";
import { ApiKeyField } from "@app/components/forms/apiKeyField";
import { SubmitButton } from "@app/components/forms/submitButton";
import { SelectField } from "@app/components/forms/selectField";
import { trackAnalyticsAndRunTool } from "@app/segment/analytics";
import { useOutputProviderContext } from "@app/context/useOutputProviderContext";
import { findContactDuplicates } from "@app/tools/contacts/findContactDuplicates/findContactDuplicatesHelper";
import type { Tool } from "@app/internal/supportToolsList/toolsList";
import { ToolHeader } from "@app/components/common/toolHeader";

export type DeduplicateField =
  "contact_name" | "email_address" | "phone_number";

export interface FormValues {
  apiKey: string;
  deduplicateBy: DeduplicateField;
}

export default function FindContactDuplicatesPage(props: { tool: Tool }) {
  const { addOutputLine, clearOutput } = useOutputProviderContext();

  return (
    <>
      <ToolHeader tool={props.tool} />
      <p className="mb-4">
        You can see an example of the generated CSV file{" "}
        <a
          target="_blank"
          href="/example-files/find-lead-duplicates/ACME%20Inc%20Lead%20name%20Duplicates.csv"
          className="text-blue-600 hover:underline hover:text-blue-800 visited:text-purple-600"
        >
          here
        </a>
        .
      </p>
      <div className="bg-blue-50 border-blue-300 border rounded p-3 mb-4 text-sm">
        <b>Note:</b> This tool will not search for duplicates across leads. For
        that - use the{" "}
        <a
          className="text-blue-600 hover:underline hover:text-blue-800 visited:text-purple-600"
          href="/tools/leads/find-lead-duplicates"
        >
          Find Lead Duplicates
        </a>{" "}
        tool.
      </div>
      <h3 className="uppercase font-bold">Contact merging</h3>
      <p className="mb-4">
        This tool will not merge or modify any contacts in your Close account.
        If you want to merge all the duplicates in bulk, please send us the
        generated CSV file to{" "}
        <a
          className="text-blue-600 hover:underline hover:text-blue-800 visited:text-purple-600"
          href="mailto:support@close.com"
        >
          support@close.com
        </a>{" "}
        and specify how should we merge them (e.g. prioritize the older contact)
        and we will take care of that for you. If conflicts exist in the contact
        name, title, or a custom field, we will keep the data from the
        prioritized contact.
      </p>
      <Formik<FormValues>
        initialValues={{
          apiKey: "",
          deduplicateBy: "contact_name",
        }}
        onSubmit={useCallback(
          async (values) => {
            await trackAnalyticsAndRunTool(
              props.tool.name,
              values,
              async () => {
                await findContactDuplicates(values, addOutputLine, clearOutput);
                return true;
              },
            );
          },
          [props, addOutputLine, clearOutput],
        )}
      >
        <Form className="flex flex-col gap-6">
          <ApiKeyField field="apiKey" permissions={["export"]} />
          <SelectField
            id="deduplicateBy"
            name="deduplicateBy"
            label="Find duplicates by"
            options={[
              {
                value: "contact_name",
                label: "Contact Name",
              },
              {
                value: "phone_number",
                label: "Phone number",
              },
              {
                value: "email_address",
                label: "Email address",
              },
            ]}
          />
          <SubmitButton>Find Duplicates (CSV)</SubmitButton>
        </Form>
      </Formik>
    </>
  );
}
